// Version: 3.10
// v3.10: Lookalike domains can be confirmed as blocked in Microsoft 365 (app.py v4.47). Phishing
//        lists the lookalikes that count against a grade (the monthly check's and the latest
//        External Cyber Security Assessment's), each with Confirm blocked in Microsoft 365: the
//        steps to block it in the client's Defender Tenant Allow/Block List (sender domain and
//        URL) and Teams external access, then tick what was done (email required) and confirm.
//        The client sees "Blocked in Microsoft 365" and what for; the IT report and the next
//        assessment show it as blocked and leave it out of the grade.
// v3.9: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v3.8: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import { useState, useEffect, useCallback, createContext, useContext } from "react";
import EmailSecurityDetail from './EmailSecurity';
import { useAuth } from './auth';

const API = "";
const token = () => localStorage.getItem("stg_token") || "";
const authFetch = (url, opts = {}) =>
  fetch(API + url, { ...opts, credentials: "include", headers: { ...(opts.headers || {}), Authorization: `Bearer ${token()}` } });

function formatDT(iso) {
  if (!iso) return null;
  try {
    const cleaned = iso.replace(/\+00:00Z$/, '+00:00').replace(/Z$/, '');
    const d = new Date(cleaned);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }) + " GMT";
  } catch { return null; }
}

// ── Acknowledgement system ────────────────────────────────────────────────────

const ACK_STATUSES = [
  "Monitoring",
  "Remediation in progress",
  "Raised with third party",
  "Accepted risk",
  "False positive",
];

const ACK_STATUS_COLORS = {
  "Monitoring":               "#86868b",
  "Remediation in progress":  "#00d4ff",
  "Raised with third party":  "#ff9f0a",
  "Accepted risk":            "#e6b800",
  "False positive":           "#30d158",
  "Blocked in Microsoft 365": "#30d158",
};
const BLOCK_PLACES = [
  ["email", "Email: sender domain blocked (Tenant Allow/Block List, Domains and addresses)"],
  ["links", "Links: URL blocked (Tenant Allow/Block List, URLs)"],
  ["teams", "Teams: domain blocked in external access"],
];
const BLOCK_PLACE_WORD = { email: "email", links: "links", teams: "Teams" };
function placesText(list) {
  const w = (list || ["email"]).map(x => BLOCK_PLACE_WORD[x]).filter(Boolean);
  return w.length > 1 ? `${w.slice(0, -1).join(", ")} and ${w[w.length - 1]}` : (w[0] || "email");
}

const ACK_KEY_PREFIX = {
  email:        "email::",
  headers:      "headers::",
  blacklist:    "blacklist::",
  phishing:     "phishing::",
  dnsmonitor:   "dnsmonitor::",
  credexposure: "credexposure::",
  buckets:      "buckets::",
  shodan:       "shodan::",
  censys:       "censys::",
  ips:          "abuseipdb::",
  takeover:     "takeover::",
  dbexposure:   "dbexposure::",
  wpscan:       "wpscan::",
  threats:      "threats::",
  portscan:     "portscan::",
};

const AckContext = createContext(null);

function useAck() {
  const ctx = useContext(AckContext);
  return ctx || { acks: {}, isAdmin: false, slug: "", clientTickets: [], setAck: async () => {}, removeAck: async () => {}, reportSummary: () => {} };
}

function AckBadge({ ack, checkKey }) {
  const { isAdmin, removeAck } = useAck();
  const color = ACK_STATUS_COLORS[ack.status] || "#86868b";
  const date  = ack.set_at
    ? new Date(ack.set_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 8, padding: "6px 10px", background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 6 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0 }}>
        STG: {ack.status}
      </span>
      {ack.status === "Blocked in Microsoft 365" && (
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
          Blocked for {placesText(ack.blocked_in)}, so it can no longer reach your staff that way
        </span>
      )}
      {date && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{date}</span>}
      {ack.ticket_ref && (
        <a href="/tickets" style={{ fontSize: 11, color: "var(--accent)", fontWeight: 600, textDecoration: "none", marginLeft: "auto" }}>
          {ack.ticket_ref} →
        </a>
      )}
      {isAdmin && (
        <button className="btn btn-quiet btn-sm" style={{ marginLeft: ack.ticket_ref ? 0 : "auto" }}
          onClick={() => removeAck(checkKey)}
          title="Remove acknowledgement"
         
        >
          Remove ×
        </button>
      )}
    </div>
  );
}

function titleFromCheckKey(checkKey) {
  const [section, ...rest] = checkKey.split("::");
  const subject = rest[rest.length - 1] || checkKey;
  const TITLES = {
    email:        () => `${(rest[0] || "").toUpperCase()} not configured — ${rest[1] || subject}`,
    headers:      () => `HTTP security headers issue — ${subject}`,
    blacklist:    () => `Email blacklist listing — ${subject}`,
    phishing:     () => `Active phishing domain — ${subject}`,
    dnsmonitor:   () => `DNS change detected — ${subject}`,
    credexposure: () => `Exposed file or credentials — ${subject}`,
    buckets:      () => `Public cloud storage bucket — ${subject}`,
    wpscan:       () => `WordPress vulnerabilities — ${subject}`,
    shodan:       () => `Internet exposure finding — ${subject}`,
    censys:       () => `Infrastructure vulnerability — ${subject}`,
    abuseipdb:    () => `IP reputation flag — ${subject}`,
    takeover:     () => `Subdomain takeover risk — ${subject}`,
    dbexposure:   () => `Database port exposed — ${subject}`,
    portscan:     () => `Critical open ports — ${subject}`,
    threats:      () => `Threat feed match — ${subject}`,
  };
  return (TITLES[section] || (() => `Security finding — ${subject}`))();
}

function AckControl({ checkKey }) {
  const { acks, isAdmin, slug, clientTickets, setAck } = useAck();
  const ack = acks[checkKey];
  const [open, setOpen]           = useState(false);
  const [status, setStatus]       = useState(ACK_STATUSES[0]);
  const [ticketRef, setTicketRef] = useState("");
  const [saving, setSaving]       = useState(false);
  const [creating, setCreating]   = useState(false);
  const [createErr, setCreateErr] = useState("");

  if (ack) return <AckBadge ack={ack} checkKey={checkKey} />;
  if (!isAdmin) return null;

  const handleSave = async (ref = ticketRef) => {
    setSaving(true);
    await setAck(checkKey, status, ref || null);
    setSaving(false);
    setOpen(false);
    setTicketRef("");
  };

  const handleCreateAndSave = async () => {
    setCreating(true);
    setCreateErr("");
    try {
      const csrf  = localStorage.getItem("stg_csrf") || "";
      const title = titleFromCheckKey(checkKey);
      const res   = await authFetch("/api/tickets", {
        method:  "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
        body:    JSON.stringify({
          title,
          description: `This ticket was raised from the Cyber Security Hub.\n\nFinding: ${checkKey}\nStatus: ${status}`,
          type:        "Incident",
          priority:    "Medium",
          client_slug: slug,
          source:      "cyber_hub",
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Failed to create ticket");
      await handleSave(d.ticket?.ref || d.ref || "");
    } catch (e) {
      setCreateErr(e.message || "Failed to create ticket");
      setCreating(false);
    }
  };

  const sel = { cursor: "pointer", flex: 1, minWidth: 0 };

  if (!open) return (
    <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }}
      onClick={() => setOpen(true)}
     
    >
      Acknowledge
    </button>
  );

  return (
    <div style={{ marginTop: 8, padding: "10px 12px", background: "var(--card-bg)", border: "1px solid var(--border)", borderRadius: 6, display: "flex", flexDirection: "column", gap: 8 }}>
      <select className="input-sm" value={status} onChange={e => setStatus(e.target.value)} style={sel}>
        {ACK_STATUSES.map(s => <option key={s}>{s}</option>)}
      </select>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <button className="btn btn-primary btn-sm" style={{ textAlign: "left" }}
          onClick={handleCreateAndSave}
          disabled={creating || saving}
         
        >
          {creating ? "Creating ticket…" : "⚑ Create ticket and acknowledge"}
        </button>
        {clientTickets.length > 0 && (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "var(--text-muted)", flexShrink: 0 }}>or link existing:</span>
            <select className="input-sm" value={ticketRef} onChange={e => setTicketRef(e.target.value)} style={{ ...sel }}>
              <option value="">Select ticket…</option>
              {clientTickets.map(t => (
                <option key={t.id} value={t.ref}>{t.ref} — {t.title?.slice(0, 40)}</option>
              ))}
            </select>
          </div>
        )}
        {createErr && <div style={{ fontSize: 11, color: "#ff2d55", marginTop: 2 }}>{createErr}</div>}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button className="btn btn-quiet btn-sm" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button className="btn btn-secondary btn-sm"
          onClick={() => handleSave()}
          disabled={saving || creating}
         
        >
          {saving ? "…" : ticketRef ? "Save with ticket" : "Acknowledge without ticket"}
        </button>
      </div>
    </div>
  );
}

// v3.10: confirm a lookalike is blocked in the client's Microsoft 365
function BlockedIn365Control({ domain }) {
  const { acks, isAdmin, setAck } = useAck();
  const checkKey = `phishing::${domain}`;
  const [open, setOpen]     = useState(false);
  const [done, setDone]     = useState({ email: false, links: false, teams: false });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState("");
  if (!isAdmin || acks[checkKey]?.status === "Blocked in Microsoft 365") return null;
  if (!open) return (
    <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} onClick={() => setOpen(true)}>
      Confirm blocked in Microsoft 365
    </button>
  );
  const confirm = async () => {
    setSaving(true); setErr("");
    const e = await setAck(checkKey, "Blocked in Microsoft 365", null, { blocked_in: Object.keys(done).filter(k => done[k]) });
    setSaving(false);
    if (e) setErr(e); else setOpen(false);
  };
  const step = { margin: "0 0 6px", fontSize: 12, lineHeight: 1.5 };
  const code = { fontFamily: "var(--font-mono)", fontSize: 11, background: "var(--surface2)", padding: "1px 5px", borderRadius: 4 };
  return (
    <div style={{ marginTop: 8, padding: "12px 14px", background: "var(--card-bg)", border: "1px solid var(--border)", borderRadius: 8, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700 }}>Block {domain} in the client's Microsoft 365</div>
      <ol style={{ margin: 0, paddingLeft: 18 }}>
        <li style={step}>
          <strong>Email.</strong> In the client's tenant open{" "}
          <a href="https://security.microsoft.com/tenantAllowBlockList?viewid=Sender" target="_blank" rel="noopener noreferrer">Microsoft Defender, Tenant Allow/Block Lists, Domains and addresses</a>,
          choose Block, enter <span style={code}>{domain}</span>, set Remove block entry after to Never expire, add the note
          {" "}<span style={code}>STG: lookalike domain</span> and Add.
        </li>
        <li style={step}>
          <strong>Links.</strong> On the{" "}
          <a href="https://security.microsoft.com/tenantAllowBlockList?viewid=Url" target="_blank" rel="noopener noreferrer">URLs tab</a>,
          Block <span style={code}>{domain}</span> and <span style={code}>*.{domain}</span>, Never expire. This stops the links in email, and in
          Teams and Office apps where Safe Links is on.
        </li>
        <li style={step}>
          <strong>Teams.</strong> In the{" "}
          <a href="https://admin.teams.microsoft.com/company-wide-settings/external-communications" target="_blank" rel="noopener noreferrer">Teams admin centre, External access</a>,
          if external domains are allowed, set it to block only specific external domains (or keep the client's allow list) and add
          {" "}<span style={code}>{domain}</span> to the blocked domains. It can take a few hours to apply.
        </li>
      </ol>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {BLOCK_PLACES.map(([k, label]) => (
          <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0 }}>
            <input type="checkbox" checked={done[k]} onChange={e => setDone(d => ({ ...d, [k]: e.target.checked }))} />
            <span style={{ fontSize: 12 }}>{label}{k === "email" ? " (required)" : ""}</span>
          </label>
        ))}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
        Once confirmed, the client sees it as blocked, and the IT report and the next External Cyber Security Assessment leave it out of the grade.
        Remove the confirmation here if the block is ever taken off.
      </div>
      {err && <div style={{ fontSize: 12, color: "#ff2d55" }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button className="btn btn-quiet btn-sm" onClick={() => setOpen(false)}>Cancel</button>
        <button className="btn btn-primary btn-sm" disabled={!done.email || saving} onClick={confirm}>
          {saving ? "…" : "Confirm blocked"}
        </button>
      </div>
    </div>
  );
}

// ── Shared UI ─────────────────────────────────────────────────────────────────

function SectionCard({ children, style }) {
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "20px 24px", marginBottom: 16, ...style }}>
      {children}
    </div>
  );
}

function StatBadge({ value, label, color }) {
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "12px 18px", minWidth: 90, flex: "1 1 90px" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: "var(--font-mono)", lineHeight: 1, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function SeverityBadge({ severity }) {
  const colors = { critical: "#ff2d55", high: "#ff9f0a", medium: "#e6b800", low: "#86868b", ok: "#30d158", info: "#86868b" };
  const color = colors[severity] || "#86868b";
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: `${color}18`, border: `1px solid ${color}40`, color, textTransform: "uppercase", letterSpacing: "0.05em" }}>{severity}</span>
  );
}

function EmptyState({ message }) {
  return <div style={{ padding: "32px 0", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>{message}</div>;
}

function NoData({ tool }) {
  return (
    <SectionCard>
      <div style={{ textAlign: "center", padding: "32px 0", color: "var(--text-muted)" }}>
        <div style={{ fontSize: 13 }}>No {tool} data yet — scans run automatically on schedule.</div>
      </div>
    </SectionCard>
  );
}

function ScannedAt({ ts }) {
  const formatted = formatDT(ts);
  if (!formatted) return null;
  return <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 16 }}>Last updated {formatted}</div>;
}

function useTool(slug, endpoint) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    authFetch(`/api/clients/${slug}/${endpoint}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, endpoint]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

// ── Trend banner ─────────────────────────────────────────────────────────────

function TrendBanner({ slug }) {
  const [trend, setTrend]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`/api/clients/${slug}/cyber-trend`)
      .then(r => r.json())
      .then(d => { setTrend(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading || !trend) return null;

  const { current, delta, prev_date } = trend;
  const issueLabel = current === 1 ? "issue across all checks" : "issues across all checks";

  const prevLabel = prev_date
    ? new Date(prev_date + "T00:00:00Z").toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" })
    : null;

  const trendColor =
    delta === null || delta === 0 ? "#86868b"
    : delta < 0 ? "#30d158"
    : "#ff9f0a";

  const trendIcon = delta === null ? null : delta < 0 ? "▼" : delta > 0 ? "▲" : "→";

  const trendText =
    delta === null   ? null
    : delta === 0    ? `Unchanged since ${prevLabel}`
    : delta < 0      ? `${Math.abs(delta)} fewer than ${prevLabel}`
    :                  `${delta} more than ${prevLabel}`;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", padding: "10px 16px", marginBottom: 20, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 24, fontWeight: 800, fontFamily: "var(--font-mono)", color: current === 0 ? "#30d158" : "#ff9f0a", lineHeight: 1 }}>
          {current}
        </span>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{issueLabel}</span>
      </div>
      {trendText && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, background: `${trendColor}14`, border: `1px solid ${trendColor}40` }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: trendColor }}>{trendIcon}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: trendColor }}>{trendText}</span>
        </div>
      )}
    </div>
  );
}


// ── Analysis strip ────────────────────────────────────────────────────────────

function AnalysisStrip({ group, summaries, acks }) {
  const sections = GROUP_SECTIONS[group] || [];
  if (sections.length <= 1) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
      {sections.map(id => {
        const s       = summaries[id];
        const label   = SECTION_LABELS[id];
        const prefix  = ACK_KEY_PREFIX[id] || "";
        const ackedCount = Object.keys(acks).filter(k => k.startsWith(prefix)).length;

        let state = "loading";
        let count = 0;

        if (s) {
          if (s.status === "nodata")  state = "nodata";
          else if (s.count === 0)     state = "clean";
          else {
            const effective = Math.max(0, s.count - ackedCount);
            state = effective === 0 ? "reviewed" : "issues";
            count = effective;
          }
        }

        const COLORS = { loading: "#86868b", nodata: "#86868b", clean: "#30d158", reviewed: "#00d4ff", issues: "#ff9f0a" };
        const LABELS = { loading: "…", nodata: "No data", clean: "Clean", reviewed: "Reviewed", issues: `${count} issue${count !== 1 ? "s" : ""}` };
        const color  = COLORS[state];

        return (
          <div key={id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 20, background: `${color}14`, border: `1px solid ${color}40` }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{label}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color }}>{LABELS[state]}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── 1. HTTP Security Headers ───────────────────────────────────────────────────
function HeadersSection({ slug }) {
  const { data, loading } = useTool(slug, "headers");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("headers", { status: "nodata", count: 0 }); return; }
      const count = data.data?.total_issues || 0;
      reportSummary("headers", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="HTTP headers" />;
  const d = data.data;
  const results = d?.results || {};
  const domains = Object.keys(results);
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={d?.targets_checked || domains.length} label="Targets"      color="var(--accent)" />
        <StatBadge value={d?.total_issues || 0}                 label="Issues"        color={d?.total_issues > 0 ? "#ff9f0a" : "#30d158"} />
        <StatBadge value={d?.total_high || 0}                   label="High Severity" color={d?.total_high > 0 ? "#ff2d55" : "var(--text-muted)"} />
      </div>
      {domains.map(domain => {
        const r = results[domain];
        const hasIssues = (r.findings || []).some(f => f.status !== "good");
        return (
          <SectionCard key={domain}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13 }}>{domain}</div>
              <div style={{ fontSize: 12, color: r.reachable ? "var(--text-muted)" : "#ff2d55" }}>{r.reachable ? `${r.score}/${r.max_score} OK` : "Unreachable"}</div>
            </div>
            {(r.findings || []).map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontSize: 14, flexShrink: 0, color: f.status === "good" ? "#30d158" : "#ff2d55" }}>{f.status === "good" ? "✓" : "✗"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{f.label}</span>
                    {f.status !== "good" && <SeverityBadge severity={f.severity} />}
                  </div>
                  {f.explanation && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{f.explanation}</div>}
                </div>
              </div>
            ))}
            {hasIssues && <AckControl checkKey={`headers::${domain}`} />}
          </SectionCard>
        );
      })}
    </div>
  );
}

// ── 2. Email Blacklists ───────────────────────────────────────────────────────
function BlacklistSection({ slug }) {
  const { data, loading } = useTool(slug, "blacklist");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("blacklist", { status: "nodata", count: 0 }); return; }
      const count = data.data?.total_listings || 0;
      reportSummary("blacklist", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="email blacklist" />;
  const d = data.data;
  const domains = d?.domains || {};
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={Object.keys(domains).length} label="Domains"       color="var(--accent)" />
        <StatBadge value={d?.total_listings || 0}      label="Blacklist Hits" color={d?.total_listings > 0 ? "#ff2d55" : "#30d158"} />
      </div>
      {Object.entries(domains).map(([domain, r]) => (
        <SectionCard key={domain}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13 }}>{domain}</div>
            <span style={{ fontSize: 12, fontWeight: 600, color: r.clean ? "#30d158" : "#ff2d55" }}>{r.clean ? "Clean" : "Listed"}</span>
          </div>
          {(r.listings || []).map((l, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{l.ip}</span>
                  {l.shared && <span style={{ fontSize: 10, marginLeft: 6, color: "#e6b800", background: "#e6b80018", border: "1px solid #e6b80040", borderRadius: 3, padding: "1px 5px" }}>Shared ({l.provider})</span>}
                </div>
                {l.listed_on?.length > 0 && <span style={{ fontSize: 11, color: l.shared ? "#e6b800" : "#ff2d55", fontWeight: 600 }}>{l.listed_on.length} list{l.listed_on.length !== 1 ? "s" : ""}</span>}
              </div>
            </div>
          ))}
          {!r.clean && <AckControl checkKey={`blacklist::${domain}`} />}
        </SectionCard>
      ))}
    </div>
  );
}

// ── 3. Phishing Detection ──────────────────────────────────────────────────────
function PhishingSection({ slug }) {
  const { data, loading } = useTool(slug, "phishing");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("phishing", { status: "nodata", count: 0 }); return; }
      // v3.10: live lookalikes and those counting against a grade (the chips take off acknowledged
      // ones, blocked in Microsoft 365 included)
      const fs = data.data?.findings || [];
      const doms = new Set([
        ...fs.filter(f => f.alive && !f.risk?.includes("PARKED_DOMAIN")).map(f => (f.domain || "").toLowerCase()),
        ...fs.filter(f => f.threat).map(f => (f.domain || "").toLowerCase()),
        ...(data.assessment_lookalikes || []),
        ...(data.blocked || []),
      ].filter(Boolean));
      reportSummary("phishing", { status: doms.size === 0 ? "clean" : "issues", count: doms.size });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="phishing detection" />;
  const d             = data.data;
  const allRegistered = d?.findings || [];
  // v3.10: the lookalikes that count against a grade: the monthly check's threats and the latest assessment's
  const byDomain = {};
  allRegistered.forEach(f => { if (f.domain) byDomain[f.domain.toLowerCase()] = f; });
  const scored = [...new Set([
    ...allRegistered.filter(f => f.threat).map(f => f.domain.toLowerCase()),
    ...(data.assessment_lookalikes || []),
    ...(data.blocked || []),   // stay listed once blocked, when they no longer count
  ])].sort();
  const scoredSet     = new Set(scored);
  const findings      = allRegistered.filter(f => f.alive && !f.risk?.includes("PARKED_DOMAIN") && !scoredSet.has((f.domain || "").toLowerCase()));
  const notLive       = allRegistered.filter(f => !findings.includes(f) && !scoredSet.has((f.domain || "").toLowerCase())
                                              && !(f.alive && !f.risk?.includes("PARKED_DOMAIN")));
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={d?.candidates_swept || 0} label="Swept"          color="var(--accent)" />
        <StatBadge value={allRegistered.length}      label="Registered"     color={allRegistered.length > 0 ? "#e6b800" : "#30d158"} />
        <StatBadge value={findings.length}           label="Live and Active" color={findings.length > 0 ? "#ff2d55" : "#30d158"} />
      </div>
      {findings.length === 0 && allRegistered.length === 0 && scored.length === 0 && <SectionCard><EmptyState message="No lookalike domains detected." /></SectionCard>}
      {scored.length > 0 && (
        <SectionCard>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#ff2d55", marginBottom: 4 }}>Lookalikes that can impersonate you</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
            They can send email and use your name or were registered recently, so they count against your grade until they are blocked in your Microsoft 365.
          </div>
          {scored.map(dom => {
            const f = byDomain[dom] || {};
            const from = [f.threat && "monthly check", (data.assessment_lookalikes || []).includes(dom) && "latest External Cyber Security Assessment"].filter(Boolean).join(" and ");
            return (
              <div key={dom} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{dom}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                  {f.has_mx === false ? "Email set up" : "Can send email"}{from ? ` · found by the ${from}` : ""}
                </div>
                <AckControl checkKey={`phishing::${dom}`} />
                <BlockedIn365Control domain={dom} />
              </div>
            );
          })}
        </SectionCard>
      )}
      {findings.length > 0 && (
        <SectionCard>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#ff2d55", marginBottom: 12 }}>Active Lookalike Domains</div>
          {findings.map((f, i) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600 }}>{f.domain}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  {f.risk?.includes("TYPOSQUATTING_PERMUTATION") && <SeverityBadge severity="high" />}
                  {f.risk?.includes("SUSPICIOUS_TLD") && <SeverityBadge severity="medium" />}
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{f.ips?.[0]} · {f.page_title || "No title"}</div>
              <AckControl checkKey={`phishing::${f.domain}`} />
            </div>
          ))}
        </SectionCard>
      )}
      {notLive.length > 0 && (
        <SectionCard>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#e6b800", marginBottom: 12 }}>Registered (Not Live)</div>
          {notLive.slice(0, 20).map((f, i) => (
            <div key={i} style={{ padding: "4px 0", borderBottom: "1px solid var(--border)", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>
              {f.domain} → {f.ips?.[0] || "no IP"}
            </div>
          ))}
        </SectionCard>
      )}
    </div>
  );
}

// ── 4. DNS Monitoring ─────────────────────────────────────────────────────────
function DNSMonitorSection({ slug }) {
  const { data, loading } = useTool(slug, "dnsmonitor");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("dnsmonitor", { status: "nodata", count: 0 }); return; }
      const count = data.data?.total_changes || 0;
      reportSummary("dnsmonitor", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="DNS monitoring" />;
  const d       = data.data;
  const changes = d?.changes || [];
  const records = d?.current_records || {};
  if (d?.first_scan) return (
    <div><ScannedAt ts={d?.scanned_at} /><SectionCard><EmptyState message="First scan complete — baseline recorded. Changes will be detected on next run." /></SectionCard></div>
  );
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={d?.total_changes || 0}    label="Changes Detected" color={d?.total_changes > 0 ? "#ff9f0a" : "#30d158"} />
        <StatBadge value={d?.critical_changes || 0} label="Critical"          color={d?.critical_changes > 0 ? "#ff2d55" : "#30d158"} />
      </div>
      {changes.length > 0 && (
        <SectionCard>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12 }}>DNS Changes Detected</div>
          {changes.map((c, i) => (
            <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <SeverityBadge severity={c.severity} />
                <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, fontSize: 13 }}>{c.domain}</span>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{c.rtype} record changed</span>
              </div>
              {c.added?.length   > 0 && <div style={{ fontSize: 11, color: "#30d158" }}>+ {c.added.join(", ")}</div>}
              {c.removed?.length > 0 && <div style={{ fontSize: 11, color: "#ff2d55" }}>- {c.removed.join(", ")}</div>}
              <AckControl checkKey={`dnsmonitor::${c.domain}`} />
            </div>
          ))}
        </SectionCard>
      )}
      {changes.length === 0 && <SectionCard><EmptyState message="No DNS changes detected since last scan." /></SectionCard>}
      {Object.entries(records).map(([domain, recs]) => (
        <SectionCard key={domain}>
          <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13, marginBottom: 10 }}>{domain} — Current Records</div>
          {Object.entries(recs).map(([rtype, vals]) => vals.length > 0 && (
            <div key={rtype} style={{ display: "flex", gap: 12, padding: "4px 0", fontSize: 11 }}>
              <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--accent)", minWidth: 32 }}>{rtype}</span>
              <span style={{ color: "var(--text-muted)", wordBreak: "break-all" }}>{vals.join(" · ")}</span>
            </div>
          ))}
        </SectionCard>
      ))}
    </div>
  );
}

// ── 5. Credential and Config Exposure ─────────────────────────────────────────
function CredExposureSection({ slug }) {
  const { data, loading } = useTool(slug, "credexposure");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("credexposure", { status: "nodata", count: 0 }); return; }
      const count = data.data?.findings?.length || 0;
      reportSummary("credexposure", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="credential exposure" />;
  const d        = data.data;
  const findings = d?.findings || [];
  const s        = d?.summary || {};
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={s.total || findings.length} label="Exposed"  color={findings.length > 0 ? "#ff9f0a" : "#30d158"} />
        <StatBadge value={s.critical || 0}            label="Critical" color={s.critical > 0 ? "#ff2d55" : "var(--text-muted)"} />
      </div>
      {findings.length === 0 ? <SectionCard><EmptyState message="No exposed credentials or sensitive files detected." /></SectionCard> : (
        <SectionCard>
          {findings.map((f, i) => (
            <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <SeverityBadge severity={f.severity} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>{f.label}</span>
                {f.credentials_detected?.length > 0 && <span style={{ fontSize: 10, color: "#ff2d55", fontWeight: 700 }}>⚠ Credentials detected</span>}
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{f.url}</div>
              {f.credentials_detected?.length > 0 && <div style={{ fontSize: 11, color: "#ff2d55", marginTop: 4 }}>{f.credentials_detected.join(", ")}</div>}
              <AckControl checkKey={`credexposure::${f.url}`} />
            </div>
          ))}
        </SectionCard>
      )}
    </div>
  );
}

// ── 6. Cloud Storage Buckets ──────────────────────────────────────────────────
function BucketsSection({ slug }) {
  const { data, loading } = useTool(slug, "buckets");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("buckets", { status: "nodata", count: 0 }); return; }
      const count = data.data?.public_count || 0;
      reportSummary("buckets", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="cloud storage" />;
  const d               = data.data;
  const findings        = d?.findings || [];
  const publicFindings  = findings.filter(f => f.public);
  const privateFindings = findings.filter(f => !f.public);
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={d?.permutations_checked || 0} label="Checked"         color="var(--accent)" />
        <StatBadge value={d?.public_count || 0}          label="Public Buckets"  color={d?.public_count > 0 ? "#ff2d55" : "#30d158"} />
        <StatBadge value={d?.private_count || 0}         label="Private Buckets" color={d?.private_count > 0 ? "#e6b800" : "var(--text-muted)"} />
      </div>
      {publicFindings.length > 0 && (
        <SectionCard>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#ff2d55", marginBottom: 12 }}>Publicly Accessible Buckets</div>
          {publicFindings.map((f, i) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600 }}>{f.bucket}</div>
                <SeverityBadge severity="critical" />
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{f.provider} · {f.url}</div>
              <AckControl checkKey={`buckets::${f.bucket}`} />
            </div>
          ))}
        </SectionCard>
      )}
      {privateFindings.length > 0 && (
        <SectionCard>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#e6b800", marginBottom: 12 }}>Existing Buckets (Private)</div>
          {privateFindings.map((f, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 12, color: "var(--text-muted)" }}>
              <span style={{ fontFamily: "var(--font-mono)" }}>{f.bucket}</span>
              <span style={{ marginLeft: 8 }}>{f.provider}</span>
            </div>
          ))}
        </SectionCard>
      )}
      {findings.length === 0 && <SectionCard><EmptyState message="No cloud storage buckets found for your brand names." /></SectionCard>}
    </div>
  );
}

// ── 7. Internet Exposure (Shodan) ─────────────────────────────────────────────
function ShodanSection({ slug }) {
  const { data, loading } = useTool(slug, "shodan");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("shodan", { status: "nodata", count: 0 }); return; }
      const count = data.data?.summary?.total_vulns || 0;
      reportSummary("shodan", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="internet exposure" />;
  const d     = data.data;
  const hosts = d?.hosts || {};
  const ips   = Object.keys(hosts).filter(ip => !hosts[ip].error);
  const s     = d?.summary || {};
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={s.hosts_checked || ips.length} label="Hosts"      color="var(--accent)" />
        <StatBadge value={s.total_ports || 0}             label="Open Ports" color={s.total_ports > 10 ? "#ff9f0a" : "var(--text-muted)"} />
        <StatBadge value={s.total_vulns || 0}             label="CVEs"       color={s.total_vulns > 0 ? "#ff2d55" : "#30d158"} />
      </div>
      {ips.map(ip => {
        const h = hosts[ip];
        return (
          <SectionCard key={ip}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13 }}>{ip}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{h.org}{h.country ? ` · ${h.country}` : ""}</div>
              </div>
              {h.vulns?.length > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: "#ff2d55", background: "#ff2d5518", border: "1px solid #ff2d5540", borderRadius: 4, padding: "2px 8px" }}>{h.vulns.length} CVE{h.vulns.length !== 1 ? "s" : ""}</span>}
            </div>
            {h.ports?.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>{h.ports.map(p => <span key={p} style={{ fontFamily: "var(--font-mono)", fontSize: 11, padding: "2px 6px", borderRadius: 4, background: "var(--card-bg)", border: "1px solid var(--border)", color: "var(--text-muted)" }}>{p}</span>)}</div>}
            {h.vulns?.length > 0 && <AckControl checkKey={`shodan::${ip}`} />}
          </SectionCard>
        );
      })}
      {ips.length === 0 && <SectionCard><EmptyState message="No internet exposure data yet." /></SectionCard>}
    </div>
  );
}

// ── 8. IP Reputation (AbuseIPDB) ──────────────────────────────────────────────
function AbuseIPDBSection({ slug }) {
  const { data, loading } = useTool(slug, "abuseipdb");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("ips", { status: "nodata", count: 0 }); return; }
      const count = data.data?.flagged || 0;
      reportSummary("ips", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="IP reputation" />;
  const d       = data.data;
  const results = d?.results || {};
  const ips     = Object.keys(results);
  const flagged = ips.filter(ip => results[ip].flagged);
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={d?.ips_checked || ips.length} label="IPs Checked" color="var(--accent)" />
        <StatBadge value={d?.flagged || flagged.length}  label="Flagged"     color={flagged.length > 0 ? "#ff2d55" : "#30d158"} />
      </div>
      {ips.length === 0 ? <SectionCard><EmptyState message="No IPs to check — run the harvester first." /></SectionCard> : (
        <SectionCard>
          {ips.map((ip, i) => {
            const r = results[ip];
            return (
              <div key={ip} style={{ padding: "10px 0", borderBottom: i < ips.length - 1 ? "1px solid var(--border)" : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 14, color: r.flagged ? "#ff2d55" : "#30d158", flexShrink: 0 }}>{r.flagged ? "⚠" : "✓"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600 }}>{ip}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.isp || ""}{r.country ? ` · ${r.country}` : ""}</div>
                  </div>
                  {r.flagged && <div style={{ textAlign: "right", flexShrink: 0 }}><div style={{ fontSize: 13, fontWeight: 700, color: "#ff2d55" }}>Score: {r.abuse_score}</div><div style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.total_reports} reports</div></div>}
                </div>
                {r.flagged && <AckControl checkKey={`abuseipdb::${ip}`} />}
              </div>
            );
          })}
        </SectionCard>
      )}
    </div>
  );
}

// ── 9. Subdomain Takeover ────────────────────────────────────────────────────
function TakeoverSection({ slug }) {
  const { data, loading } = useTool(slug, "takeover");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("takeover", { status: "nodata", count: 0 }); return; }
      const count = data.data?.vulnerable?.length || 0;
      reportSummary("takeover", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="subdomain takeover" />;
  const d          = data.data;
  const vulnerable = d?.vulnerable || [];
  const info       = d?.info || [];
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={d?.checked || 0}   label="Subdomains Checked" color="var(--accent)" />
        <StatBadge value={vulnerable.length} label="Vulnerable"          color={vulnerable.length > 0 ? "#ff2d55" : "#30d158"} />
      </div>
      {vulnerable.length === 0 && info.length === 0 && <SectionCard><EmptyState message="No subdomain takeover vulnerabilities detected." /></SectionCard>}
      {vulnerable.length > 0 && (
        <SectionCard>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#ff2d55", marginBottom: 12 }}>Vulnerable Subdomains</div>
          {vulnerable.map((v, i) => (
            <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <SeverityBadge severity={v.severity} />
                <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, fontSize: 13 }}>{v.hostname}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{v.detail}</div>
              {v.cname && <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-muted)", marginTop: 2 }}>CNAME → {v.cname}</div>}
              <AckControl checkKey={`takeover::${v.hostname}`} />
            </div>
          ))}
        </SectionCard>
      )}
      {info.length > 0 && (
        <SectionCard>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 12 }}>Cloud Service CNAMEs (Claimed)</div>
          {info.map((v, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid var(--border)", fontSize: 11, color: "var(--text-muted)" }}>
              <span style={{ fontFamily: "var(--font-mono)" }}>{v.hostname}</span>
              <span style={{ marginLeft: 8 }}>{v.service}</span>
            </div>
          ))}
        </SectionCard>
      )}
    </div>
  );
}

// ── 10. Database Exposure ─────────────────────────────────────────────────────
function DBExposureSection({ slug }) {
  const { data, loading } = useTool(slug, "dbexposure");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("dbexposure", { status: "nodata", count: 0 }); return; }
      const count = data.data?.findings?.length || 0;
      reportSummary("dbexposure", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="database exposure" />;
  const d        = data.data;
  const findings = d?.findings || [];
  const critical = findings.filter(f => f.unauthenticated);
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={d?.ips_checked || 0} label="IPs Checked"    color="var(--accent)" />
        <StatBadge value={findings.length}      label="DB Ports Open"  color={findings.length > 0 ? "#ff9f0a" : "#30d158"} />
        <StatBadge value={critical.length}      label="Unauthenticated" color={critical.length > 0 ? "#ff2d55" : "#30d158"} />
      </div>
      {findings.length === 0 ? <SectionCard><EmptyState message="No exposed database services detected." /></SectionCard> : (
        <SectionCard>
          {findings.map((f, i) => (
            <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <SeverityBadge severity={f.severity} />
                <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, fontSize: 13 }}>{f.ip}:{f.port}</span>
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{f.service}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{f.detail}</div>
              <AckControl checkKey={`dbexposure::${f.ip}:${f.port}`} />
            </div>
          ))}
        </SectionCard>
      )}
    </div>
  );
}

// ── 11. WordPress ─────────────────────────────────────────────────────────────
function WPScanSection({ slug }) {
  const { data, loading } = useTool(slug, "wpscan");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("wpscan", { status: "nodata", count: 0 }); return; }
      const count = data.data?.total_vulns || 0;
      reportSummary("wpscan", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="WordPress scan" />;
  const d       = data.data;
  const results = d?.results || {};
  const sites   = Object.keys(results);
  if (sites.length === 0) return <SectionCard><div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-muted)", fontSize: 13 }}>No WordPress sites detected on your domains.</div></SectionCard>;
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={d?.targets || sites.length} label="Sites Found"     color="var(--accent)" />
        <StatBadge value={d?.total_vulns || 0}         label="Vulnerabilities" color={d?.total_vulns > 0 ? "#ff9f0a" : "#30d158"} />
        <StatBadge value={d?.total_critical || 0}      label="Critical"        color={d?.total_critical > 0 ? "#ff2d55" : "var(--text-muted)"} />
      </div>
      {sites.map(url => {
        const r  = results[url];
        const vc = r.vuln_counts || {};
        return (
          <SectionCard key={url}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div><div style={{ fontSize: 13, fontWeight: 700 }}>{url}</div><div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>WordPress {r.wp_version}</div></div>
              <div style={{ display: "flex", gap: 4 }}>{vc.critical > 0 && <SeverityBadge severity="critical" />}{vc.high > 0 && <SeverityBadge severity="high" />}</div>
            </div>
            {(r.vulnerabilities || []).slice(0, 5).map((v, i) => (
              <div key={i} style={{ padding: "6px 0", borderTop: "1px solid var(--border)", display: "flex", gap: 8, alignItems: "flex-start" }}>
                <SeverityBadge severity={v.severity} />
                <div><div style={{ fontSize: 12, fontWeight: 600 }}>{v.title}</div><div style={{ fontSize: 11, color: "var(--text-muted)" }}>{v.component}</div></div>
              </div>
            ))}
            {vc.total === 0 && <div style={{ fontSize: 12, color: "#30d158", paddingTop: 8, borderTop: "1px solid var(--border)" }}>No vulnerabilities detected</div>}
            {vc.total > 0 && <AckControl checkKey={`wpscan::${url}`} />}
          </SectionCard>
        );
      })}
    </div>
  );
}

// ── 12. Threat Feeds (URLhaus) ────────────────────────────────────────────────
function URLhausSection({ slug }) {
  const { data, loading } = useTool(slug, "urlhaus");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available) { reportSummary("threats", { status: "nodata", count: 0 }); return; }
      const count = data.data?.urlhaus_flagged || 0;
      reportSummary("threats", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="threat intelligence" />;
  const d       = data.data;
  const urlhaus = d?.urlhaus || {};
  const allKeys = Object.keys(urlhaus);
  const flagged = allKeys.filter(k => urlhaus[k]?.flagged);
  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={allKeys.length}         label="Checked"         color="var(--accent)" />
        <StatBadge value={d?.urlhaus_flagged || 0} label="Threat Feed Hits" color={d?.urlhaus_flagged > 0 ? "#ff2d55" : "#30d158"} />
      </div>
      {flagged.length === 0 ? (
        <SectionCard><EmptyState message="No threat feed matches found — all clean." /></SectionCard>
      ) : flagged.map((key, i) => {
        const uh = urlhaus[key] || {};
        return (
          <SectionCard key={i}>
            <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{key}</div>
            {uh.flagged && <div style={{ fontSize: 11, fontWeight: 600, color: "#ff2d55" }}>{uh.url_count} URL{uh.url_count !== 1 ? "s" : ""} ({uh.online} online)</div>}
            <AckControl checkKey={`threats::${key}`} />
          </SectionCard>
        );
      })}
    </div>
  );
}

// ── Raise Ticket modal (Port Scan) ────────────────────────────────────────────
function RaiseTicketModal({ slug, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState('');
  const [title, setTitle]           = useState('Please add our public IPs for port scan monitoring');
  const [desc, setDesc]             = useState("We'd like our public IP addresses added so that port scan monitoring can be set up for our organisation.\n\nPlease could you advise on next steps or add them on our behalf?");
  const [priority, setPriority]     = useState('Medium');
  const inp = { width:'100%' };
  const lbl = { fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', display:'block', marginBottom:5 };
  const sel = { width:'100%' };
  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      const csrf = localStorage.getItem('stg_csrf') || '';
      const res  = await authFetch('/api/tickets', { method:'POST', headers: { 'Content-Type':'application/json', 'X-CSRF-Token': csrf }, body:JSON.stringify({ type:'Request', priority, title, description:desc, client_slug:slug, source:'port_scan' }) });
      const d    = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to raise ticket');
      setSubmitted(true);
    } catch(e) { setError(e.message); }
    finally { setSubmitting(false); }
  };
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',width:'100%',maxWidth:580,display:'flex',flexDirection:'column',maxHeight:'90vh',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',borderBottom:'1px solid var(--border)',flexShrink:0}}>
          <div style={{fontSize:15,fontWeight:700}}>Raise a Ticket — Port Scan Setup</div>
          <button className="btn btn-quiet btn-icon" onClick={onClose}>×</button>
        </div>
        {submitted ? (
          <div style={{padding:40,textAlign:'center'}}>
            <div style={{fontSize:36,marginBottom:12}}>✓</div>
            <div style={{fontSize:15,fontWeight:600,color:'#30d158',marginBottom:8}}>Ticket raised successfully</div>
            <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:24}}>Our team will be in touch shortly.</div>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <div style={{padding:20,display:'flex',flexDirection:'column',gap:14,overflowY:'auto'}}>
            <div><label style={lbl}>Priority</label><select className="input-sm" value={priority} onChange={e=>setPriority(e.target.value)} style={sel}>{['Low','Medium','High','Critical'].map(p=><option key={p}>{p}</option>)}</select></div>
            <div><label style={lbl}>Title</label><input className="input-sm" value={title} onChange={e=>setTitle(e.target.value)} style={inp}/></div>
            <div><label style={lbl}>Details</label><textarea className="input-sm" value={desc} onChange={e=>setDesc(e.target.value)} rows={7} style={{ ...inp, resize:'vertical' }}/></div>
            {error && <div className="error-msg">{error}</div>}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={submitting}>{submitting?<span className="spinner"/>:'Raise Ticket'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Port Scan ─────────────────────────────────────────────────────────────────
function PortScanSection({ slug }) {
  const { data, loading }               = useTool(slug, "portscan");
  const { reportSummary }               = useAck();
  const [sites, setSites]               = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [showTicket, setShowTicket]     = useState(false);

  useEffect(() => {
    authFetch(`/api/clients/${slug}/network/sites`)
      .then(r => r.json())
      .then(d => { setSites(d.sites || []); setSitesLoading(false); })
      .catch(() => setSitesLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!loading && data) {
      if (!data.available || !data.data?.available) { reportSummary("portscan", { status: "nodata", count: 0 }); return; }
      const count = data.data?.total_critical || 0;
      reportSummary("portscan", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  const hasTargets = sites.filter(s => s.public_ip).length > 0;

  return (
    <>
      <SectionCard style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Scan Targets</div>
        {sitesLoading ? (
          <div style={{ padding: "12px 0", textAlign: "center" }}><span className="spinner" /></div>
        ) : sites.filter(s => s.public_ip).length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No public IPs configured. IPs are discovered automatically from Edge node heartbeats.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {sites.filter(s => s.public_ip).map((s, i, arr) => (
              <div key={s.subnet} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < arr.length - 1 ? "1px solid var(--border)" : "none" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: s.public_ip_source === "edge" ? "#30d158" : "#e6b800" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{s.label || s.subnet}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>{s.subnet}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{s.public_ip}</div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 1 }}>{s.public_ip_source === "edge" ? "Discovered via Edge" : "Manually configured"}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
      {loading ? (
        <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>
      ) : (!data?.available || !data?.data?.available) ? (
        <>
          <SectionCard>
            <div style={{ padding: "24px 0", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
              <div style={{ marginBottom: hasTargets ? 8 : 16 }}>
                {hasTargets ? "Port scan results will appear here after the next scheduled scan." : "No port scan data yet. Public IPs are discovered automatically from Edge node heartbeats, or can be added by raising a ticket."}
              </div>
              {!hasTargets && <button className="btn btn-primary" onClick={() => setShowTicket(true)}>⚑ Raise Ticket</button>}
            </div>
          </SectionCard>
          {showTicket && <RaiseTicketModal slug={slug} onClose={() => setShowTicket(false)} />}
        </>
      ) : (() => {
        const d        = data.data;
        const results  = d?.results || [];
        const sevColor = { critical: "#ff2d55", high: "#ff9f0a", medium: "#e6b800", low: "#86868b" };
        return (
          <div>
            <ScannedAt ts={d?.scanned_at} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
              <StatBadge value={results.length}       label="Sites Scanned" color="var(--accent)" />
              <StatBadge value={d?.total_open || 0}   label="Open Ports"    color={d?.total_open > 0 ? "#ff9f0a" : "#30d158"} />
              <StatBadge value={d?.total_critical||0}  label="Critical"      color={d?.total_critical > 0 ? "#ff2d55" : "#30d158"} />
            </div>
            {results.map((r, i) => (
              <SectionCard key={i}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{r.label}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, fontFamily: "var(--font-mono)" }}>{r.ip}{r.subnet ? ` · ${r.subnet}` : ""}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: r.critical > 0 ? "#ff2d55" : r.high > 0 ? "#ff9f0a" : "#30d158" }}>{r.open_count} port{r.open_count !== 1 ? "s" : ""} open</div>
                    {r.critical > 0 && <div style={{ fontSize: 11, color: "#ff2d55" }}>{r.critical} critical</div>}
                  </div>
                </div>
                {r.ports?.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {r.ports.map((p, j) => (
                      <div key={j} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700, color: sevColor[p.severity] || "#86868b", minWidth: 40 }}>{p.port}</span>
                        <span style={{ fontSize: 12, flex: 1 }}>{p.service}</span>
                        <SeverityBadge severity={p.severity} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "#30d158" }}>No open ports detected</div>
                )}
                {r.critical > 0 && <AckControl checkKey={`portscan::${r.ip}`} />}
              </SectionCard>
            ))}
          </div>
        );
      })()}
    </>
  );
}

// ── Infrastructure Scan (Censys) ──────────────────────────────────────────────
function CensysSection({ slug }) {
  const { data, loading } = useTool(slug, "censys");
  const { reportSummary } = useAck();

  useEffect(() => {
    if (!loading && data) {
      if (!data.available || data.data?.skipped) { reportSummary("censys", { status: "nodata", count: 0 }); return; }
      const count = data.data?.summary?.total_vulns || 0;
      reportSummary("censys", { status: count === 0 ? "clean" : "issues", count });
    }
  }, [loading, data, reportSummary]);

  if (loading) return <div style={{ padding: 32, textAlign: "center" }}><span className="spinner" /></div>;
  if (!data?.available) return <NoData tool="infrastructure scan" />;
  const d       = data.data;
  if (d?.skipped) return <NoData tool="infrastructure scan" />;
  const results  = d?.results  || {};
  const summary  = d?.summary  || {};
  const domains  = d?.domains  || [];
  const allHosts = domains.flatMap(dom => (results[dom] || []).map(h => ({ ...h, domain: dom })));

  return (
    <div>
      <ScannedAt ts={d?.scanned_at} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <StatBadge value={summary.total_hosts     || 0} label="Hosts Found" color="var(--accent)" />
        <StatBadge value={summary.total_ports     || 0} label="Open Ports"  color={summary.total_ports > 10 ? "#ff9f0a" : "var(--text-muted)"} />
        <StatBadge value={summary.total_vulns     || 0} label="CVEs"        color={summary.total_vulns > 0 ? "#ff2d55" : "#30d158"} />
        <StatBadge value={summary.high_risk_hosts || 0} label="High Risk"   color={summary.high_risk_hosts > 0 ? "#ff2d55" : "#30d158"} />
      </div>
      {allHosts.length === 0 ? (
        <SectionCard><EmptyState message="No infrastructure hosts found for this domain." /></SectionCard>
      ) : allHosts.map((h, idx) => (
        <SectionCard key={`${h.domain}-${h.ip}-${idx}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13, color: "var(--text)" }}>{h.ip}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{h.asn}{h.country ? ` · ${h.country}` : ""}</div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              {h.risky_ports?.length > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: "#ff2d55", background: "#ff2d5518", border: "1px solid #ff2d5540", borderRadius: 4, padding: "2px 8px" }}>{h.risky_ports.length} high-risk port{h.risky_ports.length !== 1 ? "s" : ""}</span>}
              {h.vulns?.length      > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: "#ff2d55", background: "#ff2d5518", border: "1px solid #ff2d5540", borderRadius: 4, padding: "2px 8px" }}>{h.vulns.length} CVE{h.vulns.length !== 1 ? "s" : ""}</span>}
            </div>
          </div>
          {h.services?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: h.vulns?.length > 0 ? 10 : 0 }}>
              {h.services.map((svc, i) => (
                <span key={i} style={{ fontFamily: "var(--font-mono)", fontSize: 11, padding: "2px 8px", borderRadius: 4, background: svc.high_risk ? "#ff2d5510" : "var(--card-bg)", border: `1px solid ${svc.high_risk ? "#ff2d5540" : "var(--border)"}`, color: svc.high_risk ? "#ff2d55" : "var(--text-muted)" }}>
                  {svc.port}{svc.label ? ` · ${svc.label}` : ""}
                </span>
              ))}
            </div>
          )}
          {h.vulns?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Vulnerabilities</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {h.vulns.slice(0, 5).map((v, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "#ff2d55", fontWeight: 700, flexShrink: 0 }}>{v.cve}</span>
                    {v.product && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{v.product}{v.version ? ` ${v.version}` : ""}</span>}
                  </div>
                ))}
                {h.vulns.length > 5 && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>+{h.vulns.length - 5} more</div>}
              </div>
            </div>
          )}
          {(h.vulns?.length > 0 || h.risky_ports?.length > 0) && <AckControl checkKey={`censys::${h.ip}`} />}
        </SectionCard>
      ))}
    </div>
  );
}

// ── Group-based tab configuration ─────────────────────────────────────────────

const GROUP_TABS = [
  { id: "email_identity", label: "Email and Identity" },
  { id: "web_presence",   label: "Web Presence" },
  { id: "infrastructure", label: "Infrastructure" },
  { id: "dark_web",       label: "Dark Web and Reputation" },
];

const GROUP_SECTIONS = {
  email_identity: ["email", "blacklist", "phishing", "dnsmonitor"],
  web_presence:   ["headers", "credexposure", "buckets", "wpscan"],
  infrastructure: ["shodan", "censys", "takeover", "dbexposure", "portscan"],
  dark_web:       ["ips", "threats"],
};

const SECTION_LABELS = {
  email:        "Email authentication",
  blacklist:    "Email blacklists",
  phishing:     "Phishing and domain impersonation",
  dnsmonitor:   "DNS monitoring",
  headers:      "Web security headers",
  credexposure: "Exposed files",
  buckets:      "Cloud storage",
  wpscan:       "WordPress",
  shodan:       "Internet exposure",
  censys:       "Infrastructure scan",
  takeover:     "Subdomain takeover",
  dbexposure:   "Database exposure",
  portscan:     "Port scan",
  ips:          "IP reputation",
  threats:      "Threat feeds",
};

function SectionDivider({ label }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", padding: "20px 0 10px", borderTop: "1px solid var(--border)", marginTop: 8 }}>
      {label}
    </div>
  );
}

function TabBar({ active, onChange }) {
  return (
    <div style={{ borderBottom: "1px solid var(--border)", marginBottom: 24 }}>
      <style>{`.cyber-tab-scroll::-webkit-scrollbar { display: none; }`}</style>
      <div className="cyber-tab-scroll" style={{ display: "flex", gap: 2, overflowX: "auto", scrollbarWidth: "none" }}>
        {GROUP_TABS.map(t => (
          <button className="tab-btn" aria-selected={active === t.id} key={t.id} onClick={() => onChange(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main CyberSecurityHub ──────────────────────────────────────────────────────
export default function CyberSecurityHub({ slug }) {
  const { user }   = useAuth();
  const asClient   = new URLSearchParams(window.location.search).get('as') === 'client'
                     || sessionStorage.getItem('stg_as_client') === '1';
  const isAdmin    = user?.role === 'admin' && !asClient;

  const [subTab, setSubTab]               = useState("email_identity");
  const [acks, setAcks]                   = useState({});
  const [clientTickets, setClientTickets] = useState([]);
  const [summaries, setSummaries]         = useState({});

  const handleTabChange = useCallback((id) => {
    setSubTab(id);
    setSummaries({});
  }, []);

  useEffect(() => {
    authFetch(`/api/clients/${slug}/cyber-ack`)
      .then(r => r.json())
      .then(d => setAcks(d.acks || {}))
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!isAdmin) return;
    authFetch(`/api/tickets`)
      .then(r => r.json())
      .then(d => {
        const all = d.tickets || (Array.isArray(d) ? d : []);
        setClientTickets(all.filter(t => t.client_slug === slug && !["Resolved", "Closed"].includes(t.status)));
      })
      .catch(() => {});
  }, [slug, isAdmin]);

  const reportSummary = useCallback((id, summary) => {
    setSummaries(prev => ({ ...prev, [id]: summary }));
  }, []);

  const setAck = useCallback(async (key, status, ticketRef, extra) => {
    const csrf = localStorage.getItem("stg_csrf") || "";
    const res  = await authFetch(`/api/clients/${slug}/cyber-ack`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
      body:    JSON.stringify({ key, status, ticket_ref: ticketRef || null, ...(extra || {}) }),
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { setAcks(d.acks || {}); return null; }
    return d.error || "Could not save";   // v3.10
  }, [slug]);

  const removeAck = useCallback(async (key) => {
    const csrf = localStorage.getItem("stg_csrf") || "";
    const res  = await authFetch(`/api/clients/${slug}/cyber-ack/${encodeURIComponent(key)}`, {
      method:  "DELETE",
      headers: { "X-CSRF-Token": csrf },
    });
    if (res.ok) { const d = await res.json(); setAcks(d.acks || {}); }
  }, [slug]);

  const renderSection = (id) => {
    const onSummary = (s) => reportSummary(id, s);
    switch (id) {
      case "email":        return <EmailSecurityDetail slug={slug} hideHeader acks={acks} isAdmin={isAdmin} onAck={setAck} onUnack={removeAck} onSummary={onSummary} />;
      case "headers":      return <HeadersSection      slug={slug} />;
      case "blacklist":    return <BlacklistSection     slug={slug} />;
      case "phishing":     return <PhishingSection      slug={slug} />;
      case "dnsmonitor":   return <DNSMonitorSection    slug={slug} />;
      case "credexposure": return <CredExposureSection  slug={slug} />;
      case "buckets":      return <BucketsSection       slug={slug} />;
      case "shodan":       return <ShodanSection        slug={slug} />;
      case "censys":       return <CensysSection        slug={slug} />;
      case "ips":          return <AbuseIPDBSection     slug={slug} />;
      case "takeover":     return <TakeoverSection      slug={slug} />;
      case "dbexposure":   return <DBExposureSection    slug={slug} />;
      case "wpscan":       return <WPScanSection        slug={slug} />;
      case "threats":      return <URLhausSection       slug={slug} />;
      case "portscan":     return <PortScanSection      slug={slug} />;
      default:             return null;
    }
  };

  const sections = GROUP_SECTIONS[subTab] || [];

  return (
    <AckContext.Provider value={{ acks, isAdmin, slug, clientTickets, setAck, removeAck, reportSummary }}>
      <TrendBanner slug={slug} />
      <TabBar active={subTab} onChange={handleTabChange} />
      <AnalysisStrip group={subTab} summaries={summaries} acks={acks} />
      {sections.map((id, idx) => (
        <div key={id}>
          {idx > 0 && <SectionDivider label={SECTION_LABELS[id]} />}
          {renderSection(id)}
        </div>
      ))}
    </AckContext.Provider>
  );
}
